import { useState, useRef, useEffect } from "react";
import { apiFetch } from "../../api/api";
import { T } from "../data/Tokens";
import {
  Card, PageHeader, GoldBtn, GhostBtn, OutlineBtn,
  SearchBar, Avatar, StatusPill, Th, Td, Tr, Tabs,
  Field, Input, Select, Textarea, Toggle,
  StatCard, EmptyState, ConfirmModal, SectionTitle
} from "../components/common/Ui";

// ─── Lead Sources ──────────────────────────────────────────────────────────────
const SOURCES = [
  { id:"facebook",   label:"Facebook Ads",    icon:"🔵", color:"#1877F2" },
  { id:"instagram",  label:"Instagram",       icon:"🟣", color:"#E1306C" },
  { id:"google",     label:"Google Ads",      icon:"🔴", color:"#EA4335" },
  { id:"meta",       label:"Meta (FB+IG)",    icon:"🔷", color:"#0082FB" },
  { id:"linkedin",   label:"LinkedIn",        icon:"🔹", color:"#0A66C2" },
  { id:"whatsapp",   label:"WhatsApp",        icon:"🟢", color:"#25D366" },
  { id:"organic",    label:"Organic / SEO",   icon:"🌿", color:"#34A853" },
  { id:"referral",   label:"Referral",        icon:"🔗", color:"#FF6B35" },
  { id:"walk-in",    label:"Walk-in",         icon:"🚶", color:"#8B5CF6" },
  { id:"email",      label:"Email Campaign",  icon:"📧", color:"#F59E0B" },
  { id:"youtube",    label:"YouTube",         icon:"▶️",  color:"#FF0000" },
  { id:"pinterest",  label:"Pinterest",       icon:"📌", color:"#E60023" },
  { id:"website",    label:"Website Form",    icon:"🌐", color:"#6366F1" },
  { id:"corporate",  label:"Corporate Enquiry",icon:"🏢", color:"#374151" },
  { id:"other",      label:"Other",           icon:"📋", color:"#9CA3AF" },
];

const STATUSES = ["New", "Contacted", "Interested", "Qualified", "Converted", "Lost"];

const STATUS_COLORS = {
  New:        { bg:"#EEF3FF", c:"#5B7EE5" },
  Contacted:  { bg:"#FFF8EC", c:"#E8B04A" },
  Interested: { bg:"#FBF5EA", c:"#C8A46A" },
  Qualified:  { bg:"#EEFAF0", c:"#4CAF6E" },
  Converted:  { bg:"#EEFAF0", c:"#4CAF6E" },
  Lost:       { bg:"#FFF0F0", c:"#E27C7C" },
};

const PRODUCTS_LIST = [
  "Getting Things Done – Daily Planner",
  "Midnight Mirage – Daily Planner",
  "Daily Planner 2026",
  "Travel Planner", "Bridal Planner",
  "Wellness Journal", "Gratitude Journal",
  "Recipe Journal", "Bullet Journal",
  "Timeless Photobook", "Memory Album",
  "Corporate Gift Hamper", "Premium Gift Set",
  "Lined Notebook", "Dotted Notebook",
  "Custom / Personalised Order", "Other",
];

const EMPTY_FORM = {
  name:"", email:"", phone:"", city:"", source:"instagram", product:"",
  status:"New", budget:"", notes:"", followUp:"", campaign:"", assignedTo:"Aishcraft Team",
};

// ─── Source badge ──────────────────────────────────────────────────────────────
function SourceBadge({ sourceId }) {
  const src = SOURCES.find(s => s.id === sourceId) || SOURCES[SOURCES.length - 1];
  return (
    <span className="inline-flex items-center gap-1 sm:gap-1.5 text-[9px] sm:text-[10px] font-semibold px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-full whitespace-nowrap"
      style={{ background: src.color + "15", color: src.color }}>
      {src.icon} {src.label}
    </span>
  );
}

// ─── Lead Status Pill ──────────────────────────────────────────────────────────
function LeadStatus({ s }) {
  const st = STATUS_COLORS[s] || { bg:"#F5F5F5", c:"#9E9E9E" };
  return (
    <span className="text-[9px] sm:text-[10px] font-semibold px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-full whitespace-nowrap"
      style={{ background: st.bg, color: st.c }}>{s}</span>
  );
}

// ─── Dropdown (reuse your existing dropdown component style) ───────────────────
function SourceDropdown({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef();
  const selected = SOURCES.find(s => s.id === value) || SOURCES[0];

  useEffect(() => {
    const handler = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <label className="block text-[11px] font-semibold mb-1.5 uppercase tracking-wider" style={{ color: T.textSec }}>
        Lead Source <span style={{ color: T.danger }}>*</span>
      </label>
      <button type="button" onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-3 py-2.5 rounded-xl text-[13px] text-left"
        style={{ background: T.bgSoft, border: `1px solid ${open ? T.gold : T.border}`, color: T.textPri }}>
        <span className="flex items-center gap-2">
          <span>{selected.icon}</span>
          <span style={{ color: selected.color, fontWeight: 600 }}>{selected.label}</span>
        </span>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4"
          style={{ color: T.textSec, transform: open ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s" }}>
          <polyline points="6 9 12 15 18 9"/>
        </svg>
      </button>

      {open && (
        <div className="absolute top-full left-0 right-0 mt-1 rounded-2xl overflow-hidden z-50 py-1 max-h-64 overflow-y-auto"
          style={{ background: T.card, border: `1px solid ${T.border}`, boxShadow: "0 8px 32px rgba(0,0,0,0.12)" }}>
          {SOURCES.map(src => (
            <button key={src.id} type="button"
              onClick={() => { onChange(src.id); setOpen(false); }}
              className="w-full flex items-center gap-2.5 px-4 py-2.5 text-left text-[12px] transition-colors"
              style={{ background: value === src.id ? src.color + "10" : "", color: T.textPri }}
              onMouseEnter={e => e.currentTarget.style.background = src.color + "10"}
              onMouseLeave={e => e.currentTarget.style.background = value === src.id ? src.color + "10" : ""}>
              <span className="text-base leading-none">{src.icon}</span>
              <span style={{ color: src.color, fontWeight: 600 }}>{src.label}</span>
              {value === src.id && <span className="ml-auto text-[10px]" style={{ color: src.color }}>✓</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Status Dropdown ───────────────────────────────────────────────────────────
function StatusDropdown({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef();

  useEffect(() => {
    const handler = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const st = STATUS_COLORS[value] || { bg:"#F5F5F5", c:"#9E9E9E" };

  return (
    <div className="relative" ref={ref}>
      <label className="block text-[11px] font-semibold mb-1.5 uppercase tracking-wider" style={{ color: T.textSec }}>Status</label>
      <button type="button" onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-3 py-2.5 rounded-xl text-[13px]"
        style={{ background: st.bg, border: `1px solid ${open ? T.gold : T.border}`, color: st.c, fontWeight: 600 }}>
        {value}
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4"
          style={{ transform: open ? "rotate(180deg)" : "rotate(0)", transition: "transform 0.2s" }}>
          <polyline points="6 9 12 15 18 9"/>
        </svg>
      </button>
      {open && (
        <div className="absolute top-full left-0 right-0 mt-1 rounded-2xl overflow-hidden z-50 py-1"
          style={{ background: T.card, border: `1px solid ${T.border}`, boxShadow: "0 8px 32px rgba(0,0,0,0.12)" }}>
          {STATUSES.map(s => {
            const sc = STATUS_COLORS[s];
            return (
              <button key={s} type="button" onClick={() => { onChange(s); setOpen(false); }}
                className="w-full flex items-center gap-2.5 px-4 py-2.5 text-left text-[12px] transition-colors"
                style={{ background: value === s ? sc.bg : "" }}
                onMouseEnter={e => e.currentTarget.style.background = sc.bg}
                onMouseLeave={e => e.currentTarget.style.background = value === s ? sc.bg : ""}>
                <div className="w-2 h-2 rounded-full" style={{ background: sc.c }}/>
                <span style={{ color: sc.c, fontWeight: 600 }}>{s}</span>
                {value === s && <span className="ml-auto" style={{ color: sc.c }}>✓</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Lead Form Modal ───────────────────────────────────────────────────────────
function LeadFormModal({ lead, onClose, onSave, saving }) {
  const [form, setForm] = useState({ ...EMPTY_FORM, ...(lead || {}) });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = () => {
    if (!form.name.trim()) return alert("Name required!");
    if (!form.phone.trim()) return alert("Phone required!");
    if (!form.source) return alert("Lead source required!");
    onSave(form);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-0 sm:px-4" style={{ background: "rgba(0,0,0,0.5)" }}>
      <div className="w-full h-full sm:h-auto sm:max-w-2xl sm:max-h-[92vh] rounded-none sm:rounded-3xl overflow-hidden flex flex-col"
        style={{ background: T.card, boxShadow: "0 20px 60px rgba(0,0,0,0.2)" }}>

        {/* Header */}
        <div className="flex items-center justify-between px-4 sm:px-6 py-3.5 sm:py-4 flex-shrink-0"
          style={{ borderBottom: `1px solid ${T.border}` }}>
          <div className="min-w-0">
            <h2 className="text-[16px] sm:text-[18px] font-bold" style={{ color: T.textPri, fontFamily: T.serif }}>
              {lead?.dbId ? "Edit Lead" : "Add New Lead"}
            </h2>
            <p className="text-[11px] truncate" style={{ color: T.textSec }}>
              {lead?.dbId ? `Editing: ${lead.name}` : "Capture lead from any source"}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-2xl leading-none flex-shrink-0 ml-2">×</button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4 sm:space-y-5">

          {/* Contact Info */}
          <div className="rounded-2xl p-4" style={{ background: T.bgSoft }}>
            <SectionTitle>Contact Information</SectionTitle>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Full Name" required>
                <Input value={form.name} onChange={e => set("name", e.target.value)} placeholder="Priya Mehta" />
              </Field>
              <Field label="Phone" required>
                <Input value={form.phone} onChange={e => set("phone", e.target.value)} placeholder="+91 98200 11111" />
              </Field>
              <Field label="Email">
                <Input value={form.email} onChange={e => set("email", e.target.value)} placeholder="priya@gmail.com" type="email" />
              </Field>
              <Field label="City">
                <Input value={form.city} onChange={e => set("city", e.target.value)} placeholder="Mumbai" />
              </Field>
            </div>
          </div>

          {/* Lead Source */}
          <div className="rounded-2xl p-4" style={{ background: T.bgSoft }}>
            <SectionTitle>Lead Source & Campaign</SectionTitle>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <SourceDropdown value={form.source} onChange={v => set("source", v)} />
              <Field label="Campaign Name">
                <Input value={form.campaign} onChange={e => set("campaign", e.target.value)}
                  placeholder="Summer Sale IG / Google Search / FB Retargeting" />
              </Field>
            </div>
          </div>

          {/* Product & Deal */}
          <div className="rounded-2xl p-4" style={{ background: T.bgSoft }}>
            <SectionTitle>Product Interest & Deal</SectionTitle>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Product Interested In">
                <select value={form.product} onChange={e => set("product", e.target.value)}
                  className="w-full px-3 py-2.5 rounded-xl text-[13px] outline-none"
                  style={{ background: "#fff", border: `1px solid ${T.border}`, color: T.textPri }}>
                  <option value="">— Select Product —</option>
                  {PRODUCTS_LIST.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </Field>
              <Field label="Budget Range">
                <select value={form.budget} onChange={e => set("budget", e.target.value)}
                  className="w-full px-3 py-2.5 rounded-xl text-[13px] outline-none"
                  style={{ background: "#fff", border: `1px solid ${T.border}`, color: T.textPri }}>
                  <option value="">— Select Budget —</option>
                  <option>Under ₹500</option>
                  <option>₹500-1,000</option>
                  <option>₹1,000-1,500</option>
                  <option>₹1,500-2,000</option>
                  <option>₹2,000-3,000</option>
                  <option>₹3,000-5,000</option>
                  <option>₹5,000-10,000</option>
                  <option>₹10,000-20,000</option>
                  <option>₹20,000-50,000</option>
                  <option>₹50,000+</option>
                </select>
              </Field>
            </div>
          </div>

          {/* Status & Follow-up */}
          <div className="rounded-2xl p-4" style={{ background: T.bgSoft }}>
            <SectionTitle>Status & Follow-up</SectionTitle>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <StatusDropdown value={form.status} onChange={v => set("status", v)} />
              <Field label="Follow-up Date">
                <Input value={form.followUp} onChange={e => set("followUp", e.target.value)} type="date" />
              </Field>
              <Field label="Assigned To">
                <Input value={form.assignedTo} onChange={e => set("assignedTo", e.target.value)}
                  placeholder="Aishcraft Team" />
              </Field>
            </div>
          </div>

          {/* Notes */}
          <div className="rounded-2xl p-4" style={{ background: T.bgSoft }}>
            <SectionTitle>Notes</SectionTitle>
            <Textarea value={form.notes} onChange={e => set("notes", e.target.value)}
              placeholder="Customer requirements, conversation summary, special requests..." rows={3} />
          </div>
        </div>

        {/* Footer */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-2.5 sm:gap-2 px-4 sm:px-6 py-3.5 sm:py-4 flex-shrink-0"
          style={{ borderTop: `1px solid ${T.border}` }}>
          <button onClick={onClose} disabled={saving}
            className="px-5 py-2 rounded-xl text-[13px] font-medium border disabled:opacity-50"
            style={{ borderColor: T.border, color: T.textSec }}>Cancel</button>
          <div className="flex gap-2">
            <button onClick={handleSave} disabled={saving}
              className="flex-1 sm:flex-none px-5 py-2 rounded-xl text-[13px] font-semibold text-white disabled:opacity-60"
              style={{ background: T.gold }}>
              {saving ? "Saving..." : (lead?.dbId ? "✓ Update Lead" : "✓ Add Lead")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Lead Detail Panel (slide-in) ─────────────────────────────────────────────
function LeadDetailPanel({ lead, onClose, onEdit, onConvert, onDelete }) {
  if (!lead) return null;
  return (
    <div className="fixed inset-0 z-40 flex" style={{ background: "rgba(0,0,0,0.4)" }} onClick={onClose}>
      <div className="ml-auto w-full max-w-md h-full overflow-y-auto"
        style={{ background: T.card, boxShadow: "-8px 0 40px rgba(0,0,0,0.15)" }}
        onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="flex items-center justify-between px-4 sm:px-6 py-3.5 sm:py-4 sticky top-0 z-10"
          style={{ background: T.card, borderBottom: `1px solid ${T.border}` }}>
          <h2 className="text-[16px] font-bold" style={{ color: T.textPri, fontFamily: T.serif }}>Lead Details</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-xl">×</button>
        </div>

        <div className="p-4 sm:p-6 space-y-4 sm:space-y-5">
          {/* Profile */}
          <div className="flex items-center gap-3 sm:gap-4">
            <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl flex items-center justify-center text-[16px] sm:text-[18px] font-bold flex-shrink-0"
              style={{ background: T.goldL, color: T.goldD, border: `2px solid ${T.champagne}` }}>
              {lead.name.split(" ").map(n => n[0]).join("").slice(0,2)}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[16px] sm:text-[17px] font-bold truncate" style={{ color: T.textPri }}>{lead.name}</p>
              <p className="text-[12px] mt-0.5" style={{ color: T.textSec }}>{lead.phone}</p>
              {lead.email && <p className="text-[11px] truncate" style={{ color: T.textSec }}>{lead.email}</p>}
              <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                <LeadStatus s={lead.status} />
                <SourceBadge sourceId={lead.source} />
              </div>
            </div>
          </div>

          {/* Quick actions */}
          <div className="grid grid-cols-3 gap-2">
            <a href={`tel:${lead.phone}`}
              className="flex flex-col items-center gap-1 p-2.5 sm:p-3 rounded-2xl text-[10px] sm:text-[11px] font-medium transition-all hover:shadow-md"
              style={{ background: T.bgSoft, color: T.textSec }}>
              <span className="text-lg sm:text-xl">📞</span> Call
            </a>
            <a href={`https://wa.me/${lead.phone?.replace(/\D/g,"")}`} target="_blank" rel="noreferrer"
              className="flex flex-col items-center gap-1 p-2.5 sm:p-3 rounded-2xl text-[10px] sm:text-[11px] font-medium transition-all hover:shadow-md"
              style={{ background: "#EEFAF5", color: "#25D366" }}>
              <span className="text-lg sm:text-xl">💬</span> WhatsApp
            </a>
            <a href={`mailto:${lead.email}`}
              className="flex flex-col items-center gap-1 p-2.5 sm:p-3 rounded-2xl text-[10px] sm:text-[11px] font-medium transition-all hover:shadow-md"
              style={{ background: T.bgSoft, color: T.textSec }}>
              <span className="text-lg sm:text-xl">📧</span> Email
            </a>
          </div>

          {/* Details */}
          <div className="space-y-3 rounded-2xl p-4" style={{ background: T.bgSoft }}>
            {[
              ["📍 City", lead.city],
              ["🛍️ Interested In", lead.product],
              ["💰 Budget", lead.budget],
              ["📅 Lead Date", lead.date],
              ["📅 Follow-up", lead.followUp || "Not set"],
              ["👤 Assigned To", lead.assignedTo],
              ["📣 Campaign", lead.campaign || "—"],
            ].filter(([,v]) => v).map(([l, v]) => (
              <div key={l} className="flex items-start gap-3">
                <span className="text-[11px] font-semibold w-24 sm:w-28 flex-shrink-0" style={{ color: T.textSec }}>{l}</span>
                <span className="text-[12px] font-medium min-w-0" style={{ color: T.textPri }}>{v}</span>
              </div>
            ))}
          </div>

          {/* Notes */}
          {lead.notes && (
            <div className="rounded-2xl p-4" style={{ background: T.bgSoft }}>
              <p className="text-[10px] font-semibold uppercase tracking-wider mb-1.5" style={{ color: T.textSec }}>Notes</p>
              <p className="text-[12px] leading-relaxed" style={{ color: T.textPri }}>{lead.notes}</p>
            </div>
          )}

          {/* Pipeline stage */}
          <div className="rounded-2xl p-4" style={{ background: T.bgSoft }}>
            <p className="text-[10px] font-semibold uppercase tracking-wider mb-3" style={{ color: T.textSec }}>Pipeline Stage</p>
            <div className="flex items-center gap-1">
              {STATUSES.map((s, i) => {
                const sc = STATUS_COLORS[s];
                const isActive = STATUSES.indexOf(lead.status) >= i;
                return (
                  <div key={s} className="flex-1 flex flex-col items-center gap-1">
                    <div className="w-full h-1.5 rounded-full" style={{ background: isActive ? sc.c : T.border }}/>
                    <span className="text-[7px] sm:text-[8px] text-center leading-tight" style={{ color: isActive ? sc.c : T.textSec }}>{s}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Actions */}
          <div className="space-y-2">
            <button onClick={() => onEdit(lead)}
              className="w-full py-2.5 rounded-xl text-[13px] font-semibold text-white flex items-center justify-center gap-2"
              style={{ background: T.gold }}>
              ✏️ Edit Lead
            </button>
            {lead.status !== "Converted" && (
              <button onClick={() => onConvert(lead.dbId)}
                className="w-full py-2.5 rounded-xl text-[13px] font-semibold flex items-center justify-center gap-2"
                style={{ background: "#EEFAF0", color: "#4CAF6E", border: "1px solid #4CAF6E30" }}>
                ✓ Mark as Converted
              </button>
            )}
            <button onClick={() => onDelete(lead.dbId)}
              className="w-full py-2.5 rounded-xl text-[13px] font-semibold flex items-center justify-center gap-2"
              style={{ background: "#FFF0F0", color: T.danger, border: `1px solid ${T.danger}30` }}>
              🗑 Delete Lead
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// MAIN — LeadsAdmin
// ══════════════════════════════════════════════════════════════════════════════
export default function LeadsAdmin() {
  const [leads, setLeads]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [loadError, setLoadError] = useState("");
  const [saving, setSaving]       = useState(false);

  const [tab, setTab]             = useState("all");
  const [search, setSearch]       = useState("");
  const [filterSource, setFilterSource] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [showForm, setShowForm]   = useState(false);
  const [editing, setEditing]     = useState(null);
  const [detail, setDetail]       = useState(null);
  const [delId, setDelId]         = useState(null);
  const [view, setView]           = useState("table"); // "table" | "kanban"

  // ── Fetch leads from backend ────────────────────────────────────────────────
  const fetchLeads = async () => {
    setLoading(true);
    setLoadError("");
    try {
      const res = await apiFetch("/leads/list.php");
      setLeads(res.data || []);
    } catch (err) {
      setLoadError(err.message || "Failed to load leads.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchLeads(); }, []);

  // ── Stats ──────────────────────────────────────────────────────────────────
  const convertedCount = leads.filter(l=>l.status==="Converted").length;
  const STATS = [
    { icon:"👥", label:"Total Leads",    value: leads.length,                                     sub:"All time",             color: T.gold },
    { icon:"✨", label:"New",            value: leads.filter(l=>l.status==="New").length,          sub:"Uncontacted",          color:"#5B7EE5", bg:"#EEF3FF" },
    { icon:"📞", label:"Contacted",      value: leads.filter(l=>l.status==="Contacted").length,    sub:"In conversation",      color: T.warning, bg:"#FFF8EC" },
    { icon:"🎯", label:"Qualified",      value: leads.filter(l=>l.status==="Qualified").length,    sub:"High intent",          color: T.gold },
    { icon:"✅", label:"Converted",      value: convertedCount,                                    sub:`${leads.length ? Math.round((convertedCount/leads.length)*100) : 0}% rate`, color: T.success },
    { icon:"❌", label:"Lost",           value: leads.filter(l=>l.status==="Lost").length,         sub:"Need re-engagement",   color: T.danger, bg:"#FFF0F0" },
  ];

  const TABS = [
    { id:"all",       label:"All",       count: leads.length },
    { id:"new",       label:"New",       count: leads.filter(l=>l.status==="New").length },
    { id:"contacted", label:"Contacted", count: leads.filter(l=>l.status==="Contacted").length },
    { id:"interested",label:"Interested",count: leads.filter(l=>l.status==="Interested").length },
    { id:"qualified", label:"Qualified", count: leads.filter(l=>l.status==="Qualified").length },
    { id:"converted", label:"Converted", count: leads.filter(l=>l.status==="Converted").length },
    { id:"lost",      label:"Lost",      count: leads.filter(l=>l.status==="Lost").length },
  ];

  const filtered = leads.filter(l => {
    const ms = !search || l.name.toLowerCase().includes(search.toLowerCase()) || l.phone.includes(search) || l.email?.toLowerCase().includes(search.toLowerCase());
    const mt = tab === "all" || l.status.toLowerCase() === tab;
    const mSrc = !filterSource || l.source === filterSource;
    const mSt = !filterStatus || l.status === filterStatus;
    return ms && mt && mSrc && mSt;
  });

  // ── CRUD (backend connected) ────────────────────────────────────────────────
 const handleSave = async (formData) => {
  setSaving(true);
  try {
    // "L001" wali display id ko hata do, warna wo real numeric id ko overwrite kar degi
    const { id: _displayId, dbId, ...rest } = formData;

    if (dbId) {
      await apiFetch("/leads/update.php", {
        method: "PUT",
        body: { ...rest, id: dbId },
      });
    } else {
      await apiFetch("/leads/create.php", {
        method: "POST",
        body: rest,
      });
    }
    await fetchLeads();
    setShowForm(false);
    setEditing(null);
  } catch (err) {
    alert(err.message || "Something went wrong while saving the lead.");
  } finally {
    setSaving(false);
  }
};

  const handleDelete = async (dbId) => {
    try {
      await apiFetch("/leads/delete.php", {
        method: "DELETE",
        body: { id: dbId },
      });
      await fetchLeads();
    } catch (err) {
      alert(err.message || "Failed to delete lead.");
    } finally {
      setDelId(null);
      setDetail(null);
    }
  };

  const handleConvert = async (dbId) => {
    try {
      await apiFetch("/leads/update.php", {
        method: "PUT",
        body: { id: dbId, status: "Converted" },
      });
      await fetchLeads();
      setDetail(prev => prev ? { ...prev, status: "Converted" } : null);
    } catch (err) {
      alert(err.message || "Failed to convert lead.");
    }
  };

  const openEdit = (lead) => { setEditing(lead); setShowForm(true); setDetail(null); };

  // ── Source stats for mini chart ────────────────────────────────────────────
  const sourceCounts = SOURCES.map(src => ({
    ...src, count: leads.filter(l => l.source === src.id).length,
  })).filter(s => s.count > 0).sort((a, b) => b.count - a.count);

  return (
    <div>
      <PageHeader title="Leads" subtitle={`${leads.length} total leads across all channels`}>
        <div className="flex items-center gap-1 p-1 rounded-xl" style={{ background: T.bgSoft }}>
          {["table", "kanban"].map(v => (
            <button key={v} onClick={() => setView(v)}
              className="px-2.5 sm:px-3 py-1.5 rounded-lg text-[11px] sm:text-[12px] font-medium capitalize transition-all"
              style={view === v ? { background: "white", color: T.gold, boxShadow: "0 2px 8px rgba(0,0,0,0.08)" } : { color: T.textSec }}>
              {v === "table" ? "☰ Table" : "⬛ Pipeline"}
            </button>
          ))}
        </div>
        <GhostBtn>↓ Export CSV</GhostBtn>
        <GoldBtn onClick={() => { setEditing(null); setShowForm(true); }}>+ Add Lead</GoldBtn>
      </PageHeader>

      {/* Loading / Error states */}
      {loading && (
        <div className="flex items-center justify-center py-16">
          <div className="flex flex-col items-center gap-2">
            <div className="w-8 h-8 rounded-full animate-spin"
              style={{ border: `3px solid ${T.border}`, borderTopColor: T.gold }} />
            <p className="text-[12px]" style={{ color: T.textSec }}>Loading leads...</p>
          </div>
        </div>
      )}

      {!loading && loadError && (
        <Card className="p-6 text-center mb-5">
          <p className="text-[13px] font-semibold mb-2" style={{ color: T.danger }}>{loadError}</p>
          <button onClick={fetchLeads}
            className="px-4 py-2 rounded-xl text-[12px] font-semibold text-white"
            style={{ background: T.gold }}>
            Retry
          </button>
        </Card>
      )}

      {!loading && !loadError && (
        <>
          {/* Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-2 sm:gap-2.5 md:gap-3 mb-4 sm:mb-5 md:mb-6">
            {STATS.map(s => <StatCard key={s.label} {...s} />)}
          </div>

          {/* Source breakdown */}
          <Card className="p-3.5 sm:p-4 md:p-5 mb-4 sm:mb-5">
            <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
              <p className="text-[12px] sm:text-[13px] font-bold" style={{ color: T.textPri, fontFamily: T.serif }}>Leads by Source</p>
              <p className="text-[10px] sm:text-[11px]" style={{ color: T.textSec }}>Click source to filter</p>
            </div>
            <div className="flex flex-wrap gap-1.5 sm:gap-2">
              {sourceCounts.map(src => (
                <button key={src.id}
                  onClick={() => setFilterSource(filterSource === src.id ? "" : src.id)}
                  className="flex items-center gap-1.5 sm:gap-2 px-2 sm:px-2.5 md:px-3 py-1.5 sm:py-2 rounded-xl text-[10px] sm:text-[11px] md:text-[12px] font-semibold transition-all hover:shadow-md"
                  style={{
                    background: filterSource === src.id ? src.color + "20" : T.bgSoft,
                    border: `1.5px solid ${filterSource === src.id ? src.color : T.border}`,
                    color: src.color,
                  }}>
                  <span>{src.icon}</span>
                  <span className="hidden xs:inline">{src.label}</span>
                  <span className="ml-0.5 sm:ml-1 w-4 h-4 sm:w-5 sm:h-5 rounded-full flex items-center justify-center text-[9px] sm:text-[10px] font-bold text-white flex-shrink-0"
                    style={{ background: src.color }}>{src.count}</span>
                </button>
              ))}
              {filterSource && (
                <button onClick={() => setFilterSource("")}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-[11px] border"
                  style={{ borderColor: T.border, color: T.textSec }}>
                  × Clear filter
                </button>
              )}
            </div>
          </Card>

          <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0 mb-1">
            <Tabs tabs={TABS} active={tab} onChange={setTab} />
          </div>

          {/* ── TABLE VIEW ── */}
          {view === "table" && (
            <Card>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2.5 sm:gap-3 px-3.5 sm:px-5 py-3 sm:py-4" style={{ borderBottom: `1px solid ${T.border}` }}>
                <SearchBar value={search} onChange={setSearch} placeholder="Search by name, phone or email..." />
                <div className="flex gap-2 sm:gap-3">
                  <select value={filterSource} onChange={e => setFilterSource(e.target.value)}
                    className="flex-1 sm:flex-none text-[11px] sm:text-[12px] px-2.5 sm:px-3 py-2 sm:py-2.5 rounded-xl outline-none"
                    style={{ border: `1px solid ${T.border}`, background: "#fff", color: T.textSec, minWidth: 0 }}>
                    <option value="">All Sources</option>
                    {SOURCES.map(s => <option key={s.id} value={s.id}>{s.icon} {s.label}</option>)}
                  </select>
                  <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
                    className="flex-1 sm:flex-none text-[11px] sm:text-[12px] px-2.5 sm:px-3 py-2 sm:py-2.5 rounded-xl outline-none"
                    style={{ border: `1px solid ${T.border}`, background: "#fff", color: T.textSec }}>
                    <option value="">All Status</option>
                    {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>

              {filtered.length === 0
                ? <EmptyState icon="👥" title="No leads found" sub="Add your first lead or change filters." action="Add Lead" onAction={() => { setEditing(null); setShowForm(true); }} />
                : (
                  <>
                    {/* Desktop / tablet table (hidden on very small screens) */}
                    <div className="overflow-x-auto hidden sm:block">
                      <table className="w-full min-w-[860px]">
                        <thead>
                          <tr style={{ borderBottom: `1px solid ${T.border}` }}>
                            <Th>Lead</Th><Th>Source</Th><Th>Product</Th>
                            <Th>Budget</Th><Th>Status</Th>
                            <Th>Follow-up</Th><Th>Date</Th><Th>Actions</Th>
                          </tr>
                        </thead>
                        <tbody>
                          {filtered.map((l, i) => (
                            <Tr key={l.dbId} last={i === filtered.length - 1}>
                              <Td>
                                <div className="flex items-center gap-3 cursor-pointer" onClick={() => setDetail(l)}>
                                  <div className="w-9 h-9 rounded-xl flex items-center justify-center text-[11px] font-bold flex-shrink-0"
                                    style={{ background: T.goldL, color: T.goldD, border: `1.5px solid ${T.champagne}` }}>
                                    {l.name.split(" ").map(n => n[0]).join("").slice(0,2)}
                                  </div>
                                  <div>
                                    <p className="text-[12px] font-semibold hover:underline" style={{ color: T.textPri }}>{l.name}</p>
                                    <p className="text-[10px]" style={{ color: T.textSec }}>{l.phone}</p>
                                    {l.city && <p className="text-[10px]" style={{ color: T.textSec }}>{l.city}</p>}
                                  </div>
                                </div>
                              </Td>
                              <Td><SourceBadge sourceId={l.source} /></Td>
                              <Td>
                                <p className="text-[11px] max-w-[140px] truncate" style={{ color: T.textSec }}>{l.product || "—"}</p>
                              </Td>
                              <Td><span className="text-[11px] font-semibold" style={{ color: T.textPri }}>{l.budget || "—"}</span></Td>
                              <Td><LeadStatus s={l.status} /></Td>
                              <Td>
                                {l.followUp
                                  ? <span className="text-[11px] font-semibold" style={{ color: T.warning }}>{l.followUp}</span>
                                  : <span className="text-[11px]" style={{ color: T.textSec }}>—</span>}
                              </Td>
                              <Td><span className="text-[11px]" style={{ color: T.textSec }}>{l.date}</span></Td>
                              <Td>
                                <div className="flex gap-1">
                                  <button onClick={() => setDetail(l)}
                                    className="px-2.5 py-1.5 rounded-lg text-[11px] border hover:bg-amber-50"
                                    style={{ borderColor: T.border, color: T.textSec }}>View</button>
                                  <button onClick={() => openEdit(l)}
                                    className="px-2.5 py-1.5 rounded-lg text-[11px] border hover:bg-amber-50"
                                    style={{ borderColor: T.border, color: T.textSec }}>Edit</button>
                                  <button onClick={() => setDelId(l.dbId)}
                                    className="px-2.5 py-1.5 rounded-lg text-[11px] border hover:bg-red-50"
                                    style={{ borderColor: T.border, color: T.danger }}>🗑</button>
                                </div>
                              </Td>
                            </Tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Mobile card list (visible only on very small screens) */}
                    <div className="sm:hidden divide-y" style={{ borderColor: T.border }}>
                      {filtered.map(l => (
                        <div key={l.dbId} className="p-3.5 active:bg-black/[0.02]" onClick={() => setDetail(l)}>
                          <div className="flex items-start gap-3">
                            <div className="w-10 h-10 rounded-xl flex items-center justify-center text-[12px] font-bold flex-shrink-0"
                              style={{ background: T.goldL, color: T.goldD, border: `1.5px solid ${T.champagne}` }}>
                              {l.name.split(" ").map(n => n[0]).join("").slice(0,2)}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between gap-2">
                                <p className="text-[13px] font-semibold truncate" style={{ color: T.textPri }}>{l.name}</p>
                                <LeadStatus s={l.status} />
                              </div>
                              <p className="text-[11px] mt-0.5" style={{ color: T.textSec }}>{l.phone}{l.city ? ` · ${l.city}` : ""}</p>
                              <div className="flex items-center gap-1.5 flex-wrap mt-1.5">
                                <SourceBadge sourceId={l.source} />
                                {l.budget && <span className="text-[10px] font-semibold" style={{ color: T.textPri }}>{l.budget}</span>}
                              </div>
                              {l.product && (
                                <p className="text-[10.5px] mt-1 truncate" style={{ color: T.textSec }}>{l.product}</p>
                              )}
                              <div className="flex items-center justify-between mt-2">
                                <span className="text-[10px]" style={{ color: T.textSec }}>{l.date}</span>
                                <div className="flex gap-1.5" onClick={e => e.stopPropagation()}>
                                  <button onClick={() => openEdit(l)}
                                    className="px-2.5 py-1 rounded-lg text-[10.5px] border"
                                    style={{ borderColor: T.border, color: T.textSec }}>Edit</button>
                                  <button onClick={() => setDelId(l.dbId)}
                                    className="px-2.5 py-1 rounded-lg text-[10.5px] border"
                                    style={{ borderColor: T.border, color: T.danger }}>🗑</button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}

              <div className="px-3.5 sm:px-5 py-3 flex flex-col sm:flex-row gap-1 sm:gap-0 items-start sm:items-center justify-between" style={{ borderTop: `1px solid ${T.border}` }}>
                <p className="text-[10.5px] sm:text-[11px]" style={{ color: T.textSec }}>
                  Showing {filtered.length} of {leads.length} leads
                </p>
                <p className="text-[10.5px] sm:text-[11px] font-semibold" style={{ color: T.gold }}>
                  Conversion rate: {leads.length ? Math.round((convertedCount / leads.length) * 100) : 0}%
                </p>
              </div>
            </Card>
          )}

          {/* ── KANBAN / PIPELINE VIEW ──
              Mobile: horizontal-scroll board (columns keep a fixed min-width so cards stay readable).
              Desktop (xl+): fixed 6-column grid. */}
          {view === "kanban" && (
            <div className="flex xl:grid xl:grid-cols-6 gap-3 overflow-x-auto pb-2 xl:overflow-visible xl:pb-0 -mx-4 px-4 xl:mx-0 xl:px-0 snap-x snap-mandatory xl:snap-none">
              {STATUSES.map(status => {
                const sc = STATUS_COLORS[status];
                const stLeads = filtered.filter(l => l.status === status);
                return (
                  <div key={status} className="rounded-2xl overflow-hidden flex-shrink-0 w-[78vw] xs:w-[240px] xl:w-auto snap-start"
                    style={{ background: sc.bg + "80", border: `1px solid ${sc.c}30` }}>
                    <div className="flex items-center justify-between px-3 py-3"
                      style={{ borderBottom: `1px solid ${sc.c}20` }}>
                      <div className="flex items-center gap-2">
                        <div className="w-2.5 h-2.5 rounded-full" style={{ background: sc.c }}/>
                        <span className="text-[12px] font-bold" style={{ color: sc.c }}>{status}</span>
                      </div>
                      <span className="text-[11px] font-bold w-5 h-5 rounded-full flex items-center justify-center text-white"
                        style={{ background: sc.c, fontSize: 9 }}>{stLeads.length}</span>
                    </div>
                    <div className="p-2 space-y-2 min-h-[100px] max-h-[65vh] xl:max-h-none overflow-y-auto">
                      {stLeads.map(l => (
                        <div key={l.dbId}
                          className="rounded-xl p-3 cursor-pointer transition-all hover:shadow-md"
                          style={{ background: T.card, border: `1px solid ${T.border}` }}
                          onClick={() => setDetail(l)}>
                          <p className="text-[12px] font-semibold" style={{ color: T.textPri }}>{l.name}</p>
                          <p className="text-[10px] mt-0.5" style={{ color: T.textSec }}>{l.phone}</p>
                          <div className="mt-1"><SourceBadge sourceId={l.source} /></div>
                          {l.product && (
                            <p className="text-[10px] mt-1.5 truncate" style={{ color: T.textSec }}>{l.product}</p>
                          )}
                          {l.followUp && (
                            <p className="text-[9px] mt-1 font-semibold" style={{ color: T.warning }}>📅 {l.followUp}</p>
                          )}
                          <div className="flex gap-1 mt-2" onClick={e => e.stopPropagation()}>
                            <button onClick={() => openEdit(l)}
                              className="flex-1 py-1 rounded-lg text-[10px] border hover:bg-amber-50 transition-colors"
                              style={{ borderColor: T.border, color: T.textSec }}>Edit</button>
                            <button onClick={() => setDelId(l.dbId)}
                              className="px-2 py-1 rounded-lg text-[10px] border hover:bg-red-50 transition-colors"
                              style={{ borderColor: T.border, color: T.danger }}>🗑</button>
                          </div>
                        </div>
                      ))}
                      {stLeads.length === 0 && (
                        <div className="text-center py-4">
                          <p className="text-[10px]" style={{ color: T.textSec }}>No leads</p>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* ── Add/Edit Form Modal ── */}
      {showForm && (
        <LeadFormModal
          lead={editing}
          saving={saving}
          onClose={() => { if (!saving) { setShowForm(false); setEditing(null); } }}
          onSave={handleSave}
        />
      )}

      {/* ── Lead Detail Slide Panel ── */}
      <LeadDetailPanel
        lead={detail}
        onClose={() => setDetail(null)}
        onEdit={openEdit}
        onConvert={handleConvert}
        onDelete={(dbId) => setDelId(dbId)}
      />

      {/* ── Delete Confirm ── */}
      {delId && (
        <ConfirmModal
          title="Delete Lead?"
          message={`"${leads.find(l=>l.dbId===delId)?.name}" will be permanently deleted.`}
          onConfirm={() => handleDelete(delId)}
          onCancel={() => setDelId(null)}
        />
      )}
    </div>
  );
}